import { Box, Checkbox, FormControl, FormControlLabel, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { sxCheckbox, sxCheckboxContrast, sxMessageTextField, sxMessageTextFieldContrast } from "../../../../assets/styles/CommonSxProps";
import DefaultButton from "../../../../components/Accessibility/DefaultButton";
import DefaultText from "../../../../components/Accessibility/DefaultText";
import PageTitle from "../../../../components/Accessibility/PageTitle";
import VoltarButton from "../../../../components/Accessibility/VoltarButton";
import LoadingApp from "../../../../components/LoadingApp";
import ModalFeedBack from "../../../../components/ModalFeedBack";
import { PerguntaTriagemPaVirtual } from "../../../../interfaces/entities/PerguntaTriagemPaVirtual";
import { SolicitacaoTroca } from "../../../../interfaces/entities/UnimedAcolhe/SolicitacaoTroca";
import { getConfirmacaoTroca, getSolicitacaoTroca, salvarRespostaQuestionario } from "../../../../services/SaudeBemEstar/UnimeAcolheService";
import { useAccessibility } from "../../../../Store/contexts/AccessibilityContext";

const SolicitarTrocaPsicoterapeuta: React.FC = () => {

  const navigate = useNavigate();
  const acessibility = useAccessibility();
  const [loading, setLoading] = useState<boolean>(false);
  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: 'error' | 'success' | 'warning', title?: string, textButton?: string, subTitle?: string,
    closeAction: () => void, showButtonModal: boolean, buttonAction?: () => void
  }>(null);
  const [textQuestions, setTextQuestions] = useState<PerguntaTriagemPaVirtual>();
  const [checkBoxQuestions, setCheckBoxQuestions] = useState<PerguntaTriagemPaVirtual>();
  const [isTextValid, setIsTextValid] = useState<boolean>(false);
  const [isCheckboxValid, setIsCheckboxValid] = useState<boolean>(false);

  const fetchSolicitacaoTroca = async () => {
    setLoading(true);
    const response = await getSolicitacaoTroca();
    if (response?.status === 200) {
      const solicitacaoTroca: SolicitacaoTroca = response.data;
      setTextQuestions(solicitacaoTroca?.perguntas.find(q => q.codTipoQuestaoTeleOrient === 'TEXT'));
      setCheckBoxQuestions(solicitacaoTroca?.perguntas.find(q => q.codTipoQuestaoTeleOrient === 'CHECKBOX'));
    } else {
      console.error(response);
      openModal('error', 'Erro ao consultar informações para troca de profissional',
        'Ocorreu um erro. Tente novamente em instantes.', 'fechar', () => setModalFeedback(null), () => setModalFeedback(null));
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchSolicitacaoTroca();
  }, []);

  useEffect(() => {
    setIsCheckboxValid(checkBoxQuestions?.lstQuestoesTriagem.some(question => question.resposta === 'S') || false);
  }, [checkBoxQuestions]);

  useEffect(() => {
    setIsTextValid(textQuestions?.lstQuestoesTriagem.some(question => question.resposta?.trim() !== '') || false);
  }, [textQuestions]);

  const successMessage = `Seu pedido agora será avaliado pela equipe responsável. O prazo estimado de resposta é de até 2 dias úteis. 
  Você receberá sua resposta por meio das notificações aqui no Canal do Cliente ou pelo Aplicativo Unimed Campinas, que podem ser lidas no ícone de sino.
  Certifique-se de que as notificações estão liberadas nas configurações do seu celular.`;

  const errorMessage = `Já existe solicitação para troca de profissional`;

  const openModal = (
    icon: 'success' | 'warning' | 'error', title: string, subTitle: string, textButton: string, closeAction: () => void, buttonAction: () => void
  ) => {
    setModalFeedback({
      icon,
      title,
      subTitle,
      textButton,
      showButtonModal: true,
      closeAction: closeAction,
      buttonAction: buttonAction
    });
  }

  const confirmarSolicitacaoDeTroca = async () => {
    setLoading(true);
    const response = await getConfirmacaoTroca();
    if (response?.status === 200) {
      openModal('success', 'Solicitação realizada com sucesso!', successMessage, 'Ir para o início ', () => navigate('/saudeBemEstar/unimedAcolhe/inicio'), () => navigate('/saudeBemEstar/unimedAcolhe/inicio'));
    } else {
      openModal('warning', 'Atenção', errorMessage, 'Fechar', () => setModalFeedback(null), () => setModalFeedback(null));
      console.error(response);
    }
    setLoading(false);
  }


  const handleClickSolicitarTroca = async () => {
    setLoading(true);
    const listaPerguntas: PerguntaTriagemPaVirtual[] = [textQuestions!, checkBoxQuestions!]
    const response = await salvarRespostaQuestionario(listaPerguntas);
    if (response?.status === 200) {
      confirmarSolicitacaoDeTroca();
    } else {
      openModal('error', 'Erro ao solicitar troca de profissional',
        'Ocorreu um erro. Tente novamente em instantes ou consulte o suporte.', 'Fechar', () => setModalFeedback(null), () => setModalFeedback(null));
      console.error(response);
    }
    setLoading(false);
  }

  const toggleCheckbox = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const question = checkBoxQuestions?.lstQuestoesTriagem[index];
    if (question) {
      question.resposta = e.target.checked ? 'S' : 'N';
      setCheckBoxQuestions({ ...checkBoxQuestions, lstQuestoesTriagem: [...checkBoxQuestions?.lstQuestoesTriagem] });
    }
  }

  const handleTextAnswer = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    const question = textQuestions?.lstQuestoesTriagem[index];
    if (question) {
      question.resposta = e.target.value;
      setTextQuestions({ ...textQuestions, lstQuestoesTriagem: [...textQuestions?.lstQuestoesTriagem] });
    }
  }

  return (
    <>
      <LoadingApp showLoading={loading} />
      <div className='feature-container'>
        <PageTitle text='Solicitar troca de profissional' />
      
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <DefaultText bold text='Selecione o(s) motivo(s) para a solicitação de troca:' />
          <label id={'checkbox-options-label'} htmlFor={'checkbox-options'} aria-label={'Selecione o(s) motivo(s) para a solicitação de troca:'} />
          <FormControl 
            component="fieldset" 
            sx={{ width: '100%' }} 
            role="group" 
            id={'checkbox-options'}
            aria-labelledby={'checkbox-options-label'}
          >
          {checkBoxQuestions?.lstQuestoesTriagem.map((question, index) => (
            <FormControlLabel
              key={index}
              control={<Checkbox
                style={acessibility.contrastColorAccessibility}
                onChange={(e) => toggleCheckbox(e, index)}
                checked={question.resposta === 'S'}
              />}
              label={<DefaultText text={question.txtItemQuestao}></DefaultText>}
              sx={acessibility.contrastMode ? sxCheckboxContrast : sxCheckbox}
            />
          ))
          }
          </FormControl>
        </Box>

        {textQuestions?.lstQuestoesTriagem.map((question, index) => (
          <TextField
            key={index}
            id="outlined-textarea"
            type='text'
            value={question.resposta}
            onChange={(e) => handleTextAnswer(e, index)}
            placeholder='Digite sua justificativa'
            multiline
            style={{ width: '100%', maxWidth: '566px' }}
            sx={acessibility.contrastMode ? sxMessageTextFieldContrast : sxMessageTextField}
            inputProps={{ maxLength: 500 }}
          />
        ))
        }

        <DefaultButton text='Solicitar troca' onClick={handleClickSolicitarTroca} disabled={!isCheckboxValid} />
        <VoltarButton onClick={() => window.history.back()} />

      </div>
      <ModalFeedBack
        showModal={!!modalFeedback}
        handleShowModal={modalFeedback?.closeAction}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle as string}
        icon={modalFeedback?.icon as 'success' | 'error' | 'warning'}
        showButtonModal={modalFeedback?.showButtonModal}
        textButton={modalFeedback?.textButton as string}
        handleButtonModal={modalFeedback?.buttonAction}
      />
    </>
  )
}

export default SolicitarTrocaPsicoterapeuta;